import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

const CartContext = createContext(null);

// Pricing structure - Updated March 2026
const PRICING = {
  baseShirt: 22.99,
  // Print add-ons (no distinction between text/photo - flat rate per area)
  frontPrint: 4.00,      // Front print
  backPrint: 4.00,       // Back print
  leftSleevePrint: 3.00, // Left sleeve print
  rightSleevePrint: 3.00,// Right sleeve print
  // Shipping
  shipping: 9.99,
  // Bundle discounts
  freeShippingThreshold: 2, // 2+ shirts = free shipping
  discountThreshold: 3,     // 3+ shirts = 15% off
  discountPercent: 0.15,    // 15% discount
};

// Calculate price for a single item based on what's printed
export function calculateItemPrice(designs) {
  let price = PRICING.baseShirt;
  
  const hasFrontDesign = designs.front?.length > 0;
  const hasBackDesign = designs.back?.length > 0;
  const hasLeftDesign = designs.left?.length > 0;
  const hasRightDesign = designs.right?.length > 0;
  
  // Add print fees for each area with a design
  if (hasFrontDesign) {
    price += PRICING.frontPrint;
  }
  if (hasBackDesign) {
    price += PRICING.backPrint;
  }
  if (hasLeftDesign) {
    price += PRICING.leftSleevePrint;
  }
  if (hasRightDesign) {
    price += PRICING.rightSleevePrint;
  }
  
  return price;
}

// Get price breakdown description for a single item
export function getPriceBreakdown(designs) {
  const breakdown = [];
  breakdown.push({ label: 'Gildan 5000 Classic T-Shirt', price: PRICING.baseShirt });
  
  const hasFrontDesign = designs.front?.length > 0;
  const hasBackDesign = designs.back?.length > 0;
  const hasLeftDesign = designs.left?.length > 0;
  const hasRightDesign = designs.right?.length > 0;
  
  if (hasFrontDesign) {
    breakdown.push({ label: 'Front Print', price: PRICING.frontPrint });
  }
  if (hasBackDesign) {
    breakdown.push({ label: 'Back Print', price: PRICING.backPrint });
  }
  if (hasLeftDesign) {
    breakdown.push({ label: 'Left Sleeve Print', price: PRICING.leftSleevePrint });
  }
  if (hasRightDesign) {
    breakdown.push({ label: 'Right Sleeve Print', price: PRICING.rightSleevePrint });
  }
  
  return breakdown;
}

// Calculate cart totals with bundle discounts
export function calculateCartTotals(cartItems) {
  const totalQuantity = cartItems.reduce((sum, item) => sum + item.quantity, 0);
  const subtotal = cartItems.reduce((sum, item) => sum + (item.unitPrice * item.quantity), 0);
  
  // Bundle logic
  const hasFreeShipping = totalQuantity >= PRICING.freeShippingThreshold;
  const hasDiscount = totalQuantity >= PRICING.discountThreshold;
  
  // Calculate shipping
  const shipping = hasFreeShipping ? 0 : PRICING.shipping;
  
  // Calculate discount
  const discountAmount = hasDiscount ? subtotal * PRICING.discountPercent : 0;
  
  // Final total
  const total = subtotal - discountAmount + shipping;
  
  return {
    subtotal,
    shipping,
    hasFreeShipping,
    hasDiscount,
    discountPercent: hasDiscount ? PRICING.discountPercent * 100 : 0,
    discountAmount,
    total,
    totalQuantity,
  };
}

export function CartProvider({ children }) {
  const [cartItems, setCartItems] = useState([]);
  
  const addToCart = useCallback((item) => {
    // item: { color, colorName, size, designs, quantity, unitPrice }
    const cartItem = {
      ...item,
      id: `cart-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      addedAt: new Date().toISOString(),
    };
    setCartItems(prev => [...prev, cartItem]);
    return cartItem.id;
  }, []);
  
  const removeFromCart = useCallback((id) => {
    setCartItems(prev => prev.filter(item => item.id !== id));
  }, []);
  
  const updateCartItemQuantity = useCallback((id, quantity) => {
    if (quantity < 1) return;
    setCartItems(prev => prev.map(item => 
      item.id === id ? { ...item, quantity } : item
    ));
  }, []);
  
  const clearCart = useCallback(() => {
    setCartItems([]);
  }, []);
  
  // Calculate totals with bundle logic
  const cartTotals = useMemo(() => {
    return calculateCartTotals(cartItems);
  }, [cartItems]);
  
  const getCartTotal = useCallback(() => {
    return cartTotals.total;
  }, [cartTotals]);
  
  const getCartItemCount = useCallback(() => {
    return cartTotals.totalQuantity;
  }, [cartTotals]);
  
  const value = {
    cartItems,
    addToCart,
    removeFromCart,
    updateCartItemQuantity,
    clearCart,
    getCartTotal,
    getCartItemCount,
    cartTotals,
    PRICING,
  };
  
  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}

export { PRICING };
